import { useEffect } from 'react';

import { type UseFormSetValue } from 'react-hook-form';

import type {
  ConfirmAddressReq,
  ConfirmAddressRes,
} from '@quick/go-api-types/geo/confirm_address';

import { useFetch } from '@/layers/core/deal/services/useFetch';
import { nonNumbers } from '@/layers/core/helpers/regex';

import type { IForm } from 'components/demandForm/types.ts';

export const useConfirmAddress = ({
  index,
  debouncedAddress,
  shouldConfirm,
  city_id,
  setValue,
}: {
  index: number;
  debouncedAddress: string;
  shouldConfirm: boolean;
  city_id: string | number;
  setValue: UseFormSetValue<IForm>;
}) => {
  const swr = useFetch<ConfirmAddressRes>(
    shouldConfirm && {
      url: '/geo/confirm_address',
      method: 'POST',
      data: {
        address: debouncedAddress,
        city_id: Number(city_id),
      } satisfies ConfirmAddressReq,
      immutable: true,
    },
    {
      keepPreviousData: true,
    }
  );
  // use effect instead of on success because swr deduplicates the request and swr only runs once
  useEffect(() => {
    if (swr.data) {
      const zipCode = swr.data.zip_code?.replaceAll(nonNumbers, '');
      if (zipCode !== debouncedAddress) {
        setValue(`stops.${index}.zip`, zipCode, {
          shouldDirty: true,
        });
      }
      if (swr.data.area_geo) {
        setValue(`stops.${index}.area_geo`, swr.data.area_geo);
      }

      if (swr.data.latitude != null && swr.data.longitude != null) {
        setValue(`stops.${index}.latitude`, swr.data.latitude);
        setValue(`stops.${index}.longitude`, swr.data.longitude);
      }
    }
  }, [swr.data, setValue, index, debouncedAddress]);

  useEffect(() => {
    if (swr.error) {
      setValue(`stops.${index}.zip`, undefined, {
        shouldDirty: true,
      });
      setValue(`stops.${index}.area_geo`, undefined, {
        shouldDirty: true,
      });

      setValue(`stops.${index}.latitude`, undefined, { shouldDirty: true });
      setValue(`stops.${index}.longitude`, undefined, { shouldDirty: true });
    }
  }, [swr.error, setValue, index]);
  return swr;
};
